import { motion } from 'framer-motion';
import { QrCode, Scan, Sparkles, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useSubscription';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { formatLimit } from '@/lib/plan-limits';
import { cn } from '@/lib/utils';

interface UsageItemProps {
  icon: React.ReactNode;
  label: string;
  used: number;
  limit: number;
  colorClass: string;
}

function UsageItem({ icon, label, used, limit, colorClass }: UsageItemProps) {
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : Math.min((used / limit) * 100, 100);
  const isNearLimit = !isUnlimited && percentage >= 80;
  const isAtLimit = !isUnlimited && percentage >= 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <span className={cn("p-1.5 rounded-md", colorClass)}>
            {icon}
          </span>
          <span className="font-medium">{label}</span>
        </div>
        <span className={cn(
          "font-mono text-xs",
          isAtLimit ? "text-destructive font-semibold" : 
          isNearLimit ? "text-warning" : "text-muted-foreground"
        )}>
          {used} / {formatLimit(limit)}
        </span>
      </div>
      {!isUnlimited && (
        <Progress 
          value={percentage} 
          className={cn(
            "h-2",
            isAtLimit ? "[&>div]:bg-destructive" : 
            isNearLimit ? "[&>div]:bg-warning" : ""
          )} 
        />
      )}
      {isUnlimited && (
        <div className="h-2 bg-primary/20 rounded-full overflow-hidden">
          <div className="h-full w-full bg-gradient-to-r from-primary/50 to-primary animate-pulse" />
        </div>
      )}
    </div>
  );
}

export function UsageIndicator() {
  const { 
    usage, 
    limits, 
    plan,
    isLoading,
    getStaticQRUsagePercent,
    getDynamicQRUsagePercent,
    getScansUsagePercent
  } = useSubscription();
  const prefersReducedMotion = useReducedMotion();

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="h-5 w-32 bg-muted rounded animate-pulse" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="flex justify-between">
                <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                <div className="h-4 w-12 bg-muted rounded animate-pulse" />
              </div>
              <div className="h-2 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  const staticQRUsed = usage?.static_qr_count || 0;
  const dynamicQRUsed = usage?.dynamic_qr_count || 0;
  const scansUsed = usage?.scans_count || 0;
  const aiUsed = usage?.ai_suggestions_count || 0;

  const showUpgrade = plan === 'free' && (
    getStaticQRUsagePercent() >= 80 ||
    getDynamicQRUsagePercent() >= 80 || 
    getScansUsagePercent() >= 80 ||
    (limits.aiSuggestionsPerMonth > 0 && aiUsed >= limits.aiSuggestionsPerMonth * 0.8)
  );

  return (
    <motion.div
      initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="usage-indicator-card border-primary/20 bg-gradient-to-br from-background to-muted/30">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-4 w-4 text-primary" />
            Usage This Month
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <UsageItem
            icon={<QrCode className="h-3.5 w-3.5 text-primary" />}
            label="Static QR Codes"
            used={staticQRUsed}
            limit={limits.staticQRLimit}
            colorClass="bg-blue-500/10"
          />
          
          <UsageItem
            icon={<QrCode className="h-3.5 w-3.5 text-primary" />}
            label="Dynamic QR Codes"
            used={dynamicQRUsed}
            limit={limits.dynamicQRLimit}
            colorClass="bg-primary/10"
          />
          
          <UsageItem
            icon={<Scan className="h-3.5 w-3.5 text-secondary-foreground" />}
            label="Monthly Scans"
            used={scansUsed}
            limit={limits.scansPerMonth}
            colorClass="bg-secondary"
          />
          
          <UsageItem
            icon={<Sparkles className="h-3.5 w-3.5 text-accent-foreground" />}
            label="AI Suggestions"
            used={aiUsed}
            limit={limits.aiSuggestionsPerMonth}
            colorClass="bg-accent"
          />

          {showUpgrade && (
            <motion.div 
              initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="pt-2"
            >
              <Link to="/pricing">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full border-primary/30 hover:bg-primary/5 text-xs"
                >
                  <Zap className="h-3 w-3 mr-1.5" />
                  Upgrade for More
                </Button>
              </Link>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
